/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Modal components
import MDModal from "components/MDModal";
import MDEditModal from "components/MDEditModal";
import MDDeleteModal from "components/MDDeleteModal";

// Custom Pagination component
import Pagination from "components/common/pagination/Pagination";

// Data
import useEmpresasTableData from "pages/talento-evaluacion/gestion-usuarios/empresas/data";

// Context
import { useMaterialUIController } from "context";

// Detalles de empresa component
import DetallesEmpresa from "pages/talento-evaluacion/gestion-general/empresas-registradas/components/DetallesEmpresa"

// Componente para las acciones de cada fila
function ActionButtons({ row, onVerDetalles, onEditar, onEliminar }) {
  const empresa = row.original;
  
  return (
    <MDBox display="flex" alignItems="center" gap={1}>
      <Tooltip title="Ver Detalles" placement="top">
        <IconButton
          size="small"
          onClick={() => onVerDetalles(empresa)}
          sx={{ color: "info.main" }}
        >
          <Icon fontSize="small">visibility</Icon>
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar" placement="top">
        <IconButton
          size="small"
          onClick={() => onEditar(empresa)}
          sx={{ color: "warning.main" }}
        >
          <Icon fontSize="small">edit</Icon>
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar" placement="top">
        <IconButton
          size="small"
          onClick={() => onEliminar(empresa)}
          sx={{ color: "error.main" }}
        >
          <Icon fontSize="small">delete</Icon>
        </IconButton>
      </Tooltip>
    </MDBox>
  );
}

// PropTypes para ActionButtons
ActionButtons.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onVerDetalles: PropTypes.func.isRequired,
  onEditar: PropTypes.func.isRequired,
  onEliminar: PropTypes.func.isRequired,
};

// Componente Cell para evitar errores de PropTypes
function ActionCell({ row, onVerDetalles, onEditar, onEliminar }) {
  return (
    <ActionButtons
      row={row}
      onVerDetalles={onVerDetalles}
      onEditar={onEditar}
      onEliminar={onEliminar}
    />
  );
}

// PropTypes para ActionCell
ActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object.isRequired,
  }).isRequired,
  onVerDetalles: PropTypes.func.isRequired,
  onEditar: PropTypes.func.isRequired,
  onEliminar: PropTypes.func.isRequired,
};

const ITEMS_PER_PAGE = 5;

function Empresas() {
  const navigate = useNavigate();
  const { columns, rows, setRows } = useEmpresasTableData();
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller;

  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Filter rows based on searchTerm
  const filteredRows = useMemo(() => {
    if (!searchTerm) {
      return rows;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return rows.filter((row) => {
      const { nombre, email, sector_empresa } = row.original || {};
      return (
        (nombre && nombre.toLowerCase().includes(lowercasedSearchTerm)) ||
        (email && email.toLowerCase().includes(lowercasedSearchTerm)) ||
        (sector_empresa && sector_empresa.toLowerCase().includes(lowercasedSearchTerm))
      );
    });
  }, [rows, searchTerm]);

  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  const handleVerDetalles = (empresa) => {
    setSelectedEmpresa(empresa);
    setOpenModal(true);
  };

  const handleEditar = (empresa) => {
    setSelectedEmpresa(empresa);
    setOpenEditModal(true);
  };

  const handleEliminar = (empresa) => {
    setSelectedEmpresa(empresa);
    setOpenDeleteModal(true);
  };

  const handleSaveEdit = (updatedEmpresa) => {
    // Actualizar la empresa en el estado
    setRows(prevRows =>
      prevRows.map(row => {
        if (row.original?.id === updatedEmpresa.id) {
          return {
            ...row,
            original: updatedEmpresa,
          };
        }
        return row;
      })
    );

    setSnackbarMessage(`Empresa "${updatedEmpresa.nombre}" actualizada exitosamente`);
    openSuccessSB();
  };

  const handleDelete = (empresa) => {
    // Eliminar la empresa del estado
    setRows(prevRows =>
      prevRows.filter(row => row.original?.id !== empresa.id)
    );

    setSnackbarMessage(`Empresa "${empresa.nombre}" eliminada exitosamente`);
    openSuccessSB();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEmpresa(null);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedEmpresa(null);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setSelectedEmpresa(null);
  };

  // Reset page to 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Pagination logic
  const totalItems = filteredRows.length;
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Empresas
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="white"
                  size="small"
                  onClick={() => navigate("/gestion-usuarios/nueva-empresa")}
                  sx={{
                    color: "info.main",
                    fontWeight: "bold",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                    },
                  }}
                >
                  <Icon sx={{ mr: 1 }}>add</Icon>
                  Nueva Empresa
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns: columns.map(col =>
                      col.accessor === "accion"
                        ? {
                            ...col,
                            Cell: (props) => (
                              <ActionCell
                                {...props}
                                onVerDetalles={handleVerDetalles}
                                onEditar={handleEditar}
                                onEliminar={handleEliminar}
                              />
                            ),
                          }
                        : col
                    ),
                    rows: paginatedRows,
                  }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
                <Pagination
                  itemsPerPage={ITEMS_PER_PAGE}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal para ver detalles */}
      <MDModal
        open={openModal}
        onClose={handleCloseModal}
        title="Detalles de la Empresa"
        size="lg"
      >
        {selectedEmpresa && (
          <DetallesEmpresa empresa={selectedEmpresa} />
        )}
      </MDModal>

      {/* Modal para editar */}
      <MDEditModal
        open={openEditModal}
        onClose={handleCloseEditModal}
        empresa={selectedEmpresa}
        onSave={handleSaveEdit}
      />

      {/* Modal para eliminar */}
      <MDDeleteModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        empresa={selectedEmpresa}
        onDelete={handleDelete}
      />

      {/* Snackbar para notificaciones */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Éxito"
        content={snackbarMessage}
        dateTime=""
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />

      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content={snackbarMessage}
        dateTime=""
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default Empresas; 